import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../common/Modal";
import Tabs from "../common/Tabs";
import EmptyState from "../common/EmptyState";
import { getUserId, getUserName, getUserAvatar } from "../../utils/chatHelpers";
import { color } from "../../styles/theme";

/**
 * "Who has seen/received this message" — ported from mobile's
 * `message-info-updated` screen, as a modal instead of a full page (better
 * fit for web). `participants` is the room's full member list (from
 * ChatContext, the same list ParticipantsStrip uses) — filtered here by the
 * message's own sender id rather than blindly subtracting 1, since nothing
 * guarantees the sender is (or isn't) counted the same way on every payload.
 */
const MessageInfoModal = ({ message, participants, onClose }) => {
  const { t } = useTranslation("chat");
  const [tab, setTab] = useState("read");
  const [showRemaining, setShowRemaining] = useState(false);

  const seenBy = message.seenBy || [];
  const deliveredTo = message.deliveredTo || [];
  const list = tab === "read" ? seenBy : deliveredTo;

  const pending = useMemo(() => {
    const listedIds = new Set(list.map((u) => String(getUserId(u))));
    return (participants || []).filter((p) => {
      const id = getUserId(p);
      return String(id) !== String(message.senderId) && !listedIds.has(String(id));
    });
  }, [participants, list, message.senderId]);

  const tabs = [
    { key: "read", label: t("messageInfo.readTabCount", { count: seenBy.length }) },
    { key: "delivered", label: t("messageInfo.deliveredTabCount", { count: deliveredTo.length }) },
  ];

  const renderUserRow = (u, i, { at } = {}) => {
    const name = getUserName(u) || "?";
    const avatar = getUserAvatar(u);
    return (
      <div key={getUserId(u) ?? i} style={s.row}>
        {avatar ? <img src={avatar} alt="" style={s.avatar} /> : <div style={s.avatarFallback}>{name.charAt(0).toUpperCase()}</div>}
        <span style={s.name}>{name}</span>
        {at && <span style={s.time}>{new Date(at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>}
      </div>
    );
  };

  return (
    <Modal title={t("messageInfo.title")} onClose={onClose} maxWidth={420}>
      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {list.length === 0 && pending.length === 0 ? (
        <EmptyState text={t("messageInfo.noneYet")} />
      ) : (
        <>
          {list.length === 0 ? (
            <EmptyState text={t("messageInfo.noneYet")} />
          ) : (
            <div style={s.list}>
              {list.map((u, i) => renderUserRow(u, i, { at: tab === "read" ? (u.seenAt ?? u.SeenAt) : (u.deliveredAt ?? u.DeliveredAt) }))}
            </div>
          )}

          {pending.length > 0 && (
            <button type="button" style={s.remainingBtn} onClick={() => setShowRemaining((v) => !v)}>
              {t("messageInfo.remaining", { count: pending.length })} {showRemaining ? "▲" : "▼"}
            </button>
          )}

          {showRemaining && pending.length > 0 && (
            <div style={s.list}>
              {pending.map((u, i) => renderUserRow(u, i))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

const s = {
  list: { display: "flex", flexDirection: "column", gap: 10, marginTop: 4 },
  row: { display: "flex", alignItems: "center", gap: 10 },
  avatar: { width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0 },
  avatarFallback: {
    width: 30, height: 30, borderRadius: "50%", background: color.primary50, color: color.primary600,
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12, flexShrink: 0,
  },
  name: { fontSize: 13, color: color.textPrimary, fontWeight: 500, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  time: { fontSize: 11, color: color.textMuted, flexShrink: 0 },
  remainingBtn: {
    display: "block", width: "100%", marginTop: 14, fontSize: 11.5, color: color.primary600,
    textAlign: "center", background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: "4px 0",
  },
};

export default MessageInfoModal;
